'use client';

import * as React from 'react';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { cn } from "@/lib/utils";
import { Link } from 'react-router-dom';

interface ResourceBadgeProps {
  /** Display name for the resource (e.g., "Order", "Payment") */
  name: string;
  /** The ID or value to display */
  value: string;
  /** Internal app route (uses React Router) */
  appLink?: string;
  /** External URL (opens in new tab) */
  externalLink?: string;
  /** Badge variant */
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  /** Additional CSS classes */
  className?: string;
}

/**
 * ResourceBadge component for displaying resource IDs in tables
 * Clicking navigates to the resource detail page
 */
export function ResourceBadge({
  name,
  value,
  appLink,
  externalLink,
  variant = 'secondary',
  className,
}: ResourceBadgeProps) {
  const finalLink = appLink ?? externalLink;
  if (!finalLink) {
    return (
      <Badge variant={variant} className={className}>
        <span className="truncate" title={`${name}: ${value}`}>
          {value}
        </span>
      </Badge>
    );
  }

  return (
    <Link to={finalLink}>
      <Badge
        variant={variant}
        className={cn('group cursor-pointer gap-1.5 transition-all hover:bg-opacity-80', className)}
      >
        <span className="truncate" title={`${name}: ${value}`}>
          {value}
        </span>
        {externalLink ? (
          <ExternalLink className="h-3 w-3 opacity-70 transition-opacity group-hover:opacity-100" />
        ) : (
          <ArrowRight className="h-3 w-3 opacity-70 transition-opacity group-hover:opacity-100" />
        )}
      </Badge>
    </Link>
  );
}
